import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Rating,
  Chip,
  CircularProgress,
  Alert,
  Container,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Stack,
  Paper,
  Divider,
} from "@mui/material";
import { Helmet } from "react-helmet";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import StarIcon from "@mui/icons-material/Star";
import LocalDiningIcon from "@mui/icons-material/LocalDining";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  fetchAllRestaurants,
  selectAllRestaurants,
  selectRestaurantsLoading,
  selectRestaurantsError,
  selectRestaurantsPagination,
} from "../redux/slices/publicSlice";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const THEME = {
  primary: "#FF6B00",
  primaryDark: "#E85A00",
  gradient: "linear-gradient(135deg, #FF6B00 0%, #FF9F43 100%)",
  bg: "#FFF9F3",
  cardHover: "0 20px 40px -12px rgba(255, 107, 0, 0.25)",
};

const LIMIT = 8;

const Restaurant = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  // ✅ Page load pe location detect karo
  // useEffect(() => {
  //   // Sirf ek baar chalao
  //   if (locationDetected) return;

  //   if (!navigator.geolocation) {
  //     console.log("Geolocation not supported");
  //     return;
  //   }

  //   // Permission check karo pehle (agar pehle se granted hai to direct lo)
  //   navigator.permissions
  //     ?.query({ name: "geolocation" })
  //     .then((result) => {
  //       if (result.state === "granted") {
  //         // Permission already granted, direct location lo
  //         getCurrentLocation();
  //       } else if (result.state === "prompt") {
  //         // Permission prompt hai, request karo
  //         getCurrentLocation();
  //       } else {
  //         // Permission denied hai, kuch mat karo
  //         console.log("Location permission denied");
  //       }
  //     })
  //     .catch(() => {
  //       // Permissions API fail ho to bhi try karo
  //       getCurrentLocation();
  //     });
  // }, []);

  // const getCurrentLocation = () => {
  //   navigator.geolocation.getCurrentPosition(
  //     async (position) => {
  //       try {
  //         const { latitude, longitude } = position.coords;

  //         // Reverse geocoding
  //         const res = await fetch(
  //           `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
  //         );
  //         const data = await res.json();

  //         const detectedCity =
  //           data.address?.city ||
  //           data.address?.town ||
  //           data.address?.village ||
  //           data.address?.county ||
  //           "";

  //         if (detectedCity) {
  //           setCity(detectedCity);
  //           setLocationDetected(true);
  //         }
  //       } catch (err) {
  //         console.error("Reverse geocoding error:", err);
  //       }
  //     },
  //     (error) => {
  //       console.log("Location error:", error.message);
  //     },
  //     { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
  //   );
  // };

  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);

  const restaurants = useSelector(selectAllRestaurants);
  const loading = useSelector(selectRestaurantsLoading);
  const error = useSelector(selectRestaurantsError);
  const pagination = useSelector(selectRestaurantsPagination);

  const [locationDetected, setLocationDetected] = useState(false);

  const loadMoreRef = useRef(null);

  // ✅ Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // ✅ Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, state, city]);

  // ✅ Fetch — state aur city dono bhejo
  useEffect(() => {
    dispatch(
      fetchAllRestaurants({
        page,
        limit: LIMIT,
        search: debouncedSearch,
        state,
        city,
      }),
    );
  }, [dispatch, page, debouncedSearch, state, city]);

  // ✅ Unique states from restaurant data
  const states = useMemo(() => {
    const unique = [
      ...new Set(
        restaurants
          .map((r) => r.state)
          .filter(Boolean)
          .map((s) => s.trim()),
      ),
    ];
    return unique.sort((a, b) => a.localeCompare(b));
  }, [restaurants]);

  // ✅ Cities filtered by selected state (ya sab cities agar state nahi chuni)
  const cities = useMemo(() => {
    const source = state
      ? restaurants.filter((r) => r.state === state)
      : restaurants;

    const unique = [
      ...new Set(
        source
          .map((r) => r.city)
          .filter(Boolean)
          .map((c) => c.trim()),
      ),
    ];
    return unique.sort((a, b) => a.localeCompare(b));
  }, [restaurants, state]);

  // ✅ When state changes, reset city (agar city us state me nahi hai)
  useEffect(() => {
    if (city && !cities.includes(city)) {
      setCity("");
    }
  }, [cities, city]);

  // ✅ Client-side fallback filter (name + state + city)
  const filteredRestaurants = useMemo(() => {
    let list = restaurants;

    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      list = list.filter((r) =>
        (r.restaurantName || "").toLowerCase().includes(q),
      );
    }

    if (state) {
      list = list.filter((r) => r.state === state);
    }

    if (city) {
      list = list.filter((r) => r.city === city);
    }

    return list;
  }, [restaurants, debouncedSearch, state, city]);

  // const handleViewMenu = (slug, id) => navigate(`/restaurant/${slug || id}/menu`);
  // navigate(`/restaurant/${id}/menu`);
  const handleViewMenu = (restaurant) => {
    const identifier = restaurant.slug || restaurant._id;
    navigate(`/restaurant/${identifier}/menu`);
  };

  const totalPages = pagination?.totalPages || 1;
  const hasMore = page < totalPages;

  // ✅ Infinite scroll
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loading) {
          setPage((prev) => prev + 1);
        }
      },
      { rootMargin: "200px", threshold: 0.1 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, loading]);

  const isInitialLoading = loading && restaurants.length === 0 && page === 1;

  if (isInitialLoading) {
    return (
      <>
        <Navbar />
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: THEME.bg,
          }}
        >
          <CircularProgress sx={{ color: THEME.primary }} />
        </Box>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <Container sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          Restaurants Near You | Explore Menus & Order Online | RestaurantWala
        </title>

        <meta
          name="description"
          content="Discover restaurants on RestaurantWala. Explore restaurant menus, food options, offers and locations, and order directly online."
        />

        <meta name="robots" content="index, follow" />

        <link
          rel="canonical"
          href={`https://restaurantwala.in${window.location.pathname}`}
        />

        <meta
          property="og:title"
          content="Explore Restaurants | RestaurantWala"
        />

        <meta
          property="og:description"
          content="Discover restaurants, explore menus and order food directly online with RestaurantWala."
        />

        <meta
          property="og:url"
          content={`https://restaurantwala.in${window.location.pathname}`}
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:image"
          content="https://restaurantwala.in/logo512.png"
        />

        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Navbar />

      {/* ================= HERO SECTION ================= */}
      <Box
        sx={{
          position: "relative",
          background: THEME.gradient,
          color: "#fff",
          pt: { xs: 6, md: 8 },
          pb: { xs: 8, md: 12 },
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -80,
            right: -80,
            width: 320,
            height: 320,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -120,
            left: -60,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
          },
        }}
      >
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Chip
              icon={<LocalDiningIcon sx={{ color: "#fff !important" }} />}
              label="Discover • Order • Enjoy"
              sx={{
                bgcolor: "rgba(255,255,255,0.18)",
                color: "#fff",
                fontWeight: 600,
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            />
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                fontSize: { xs: "2rem", md: "3.2rem" },
                lineHeight: 1.15,
                letterSpacing: "-0.5px",
              }}
            >
              Find Your Favorite
              <Box component="span" sx={{ display: "block", opacity: 0.95 }}>
                Restaurants Near You
              </Box>
            </Typography>
            <Typography
              sx={{
                maxWidth: 620,
                opacity: 0.9,
                fontSize: { xs: "0.95rem", md: "1.05rem" },
              }}
            >
              Explore top-rated restaurants, discover new cuisines and order
              your favourite meals — all in one place.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* ================= SEARCH CARD ================= */}
      <Container
        maxWidth="xl"
        sx={{ mt: { xs: -5, md: -6 }, position: "relative", zIndex: 2 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 4,
            bgcolor: "#fff",
            boxShadow: "0 20px 50px -20px rgba(0,0,0,0.15)",
            border: "1px solid rgba(0,0,0,0.04)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
          >
            {/* Search */}
            <TextField
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search restaurant by name..."
              size="medium"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: THEME.primary }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  bgcolor: "#FFF9F3",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: THEME.primary + "55" },
                  "&.Mui-focused fieldset": { borderColor: THEME.primary },
                },
              }}
            />

            {/* State */}
            <TextField
              select
              value={state}
              onChange={(e) => {
                setState(e.target.value);
                setCity(""); // ✅ state change → city reset
              }}
              label="State"
              size="medium"
              sx={{
                minWidth: { xs: "100%", md: 220 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "#FFF9F3",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: THEME.primary + "55" },
                  "&.Mui-focused fieldset": { borderColor: THEME.primary },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ color: THEME.primary }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">All States</MenuItem>
              {states.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            {/* City */}
            <TextField
              select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              label="City"
              size="medium"
              disabled={cities.length === 0}
              sx={{
                minWidth: { xs: "100%", md: 220 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "#FFF9F3",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: THEME.primary + "55" },
                  "&.Mui-focused fieldset": { borderColor: THEME.primary },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ color: THEME.primary }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">All Cities</MenuItem>
              {cities.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Paper>
      </Container>

      {/* ================= RESTAURANTS GRID ================= */}
      <Box sx={{ bgcolor: THEME.bg, py: { xs: 5, md: 7 }, minHeight: "60vh" }}>
        <Container maxWidth="xl">
          {/* Section header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
            sx={{ mb: 4 }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ color: "#1a1a1a" }}
              >
                {city
                  ? `Restaurants in ${city}`
                  : state
                    ? `Restaurants in ${state}`
                    : "Popular Restaurants"}
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={3}>
            {filteredRestaurants.length > 0 ? (
              filteredRestaurants.map((restaurant) => (
                <Grid
                  item
                  size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                  key={restaurant._id}
                >
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 4,
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                      display: "flex",
                      flexDirection: "column",
                      boxShadow: "0 4px 20px -8px rgba(0,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.04)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: THEME.cardHover,
                        "& .restaurant-img": {
                          transform: "scale(1.08)",
                        },
                      },
                    }}
                  >
                    {/* Image wrapper */}
                    <Box sx={{ position: "relative", overflow: "hidden" }}>
                      <CardMedia
                        component="img"
                        height="200"
                        className="restaurant-img"
                        image={restaurant?.profileImage?.url || "/logo512.png"}
                        alt={restaurant.restaurantName || "Restaurant"}
                        sx={{
                          objectFit: "cover",
                          transition: "transform 0.6s ease",
                        }}
                      />

                      {/* Gradient overlay */}
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 45%)",
                          pointerEvents: "none",
                        }}
                      />

                      {/* Rating badge */}
                      <Chip
                        icon={
                          <StarIcon
                            sx={{ color: "#FFB400 !important", fontSize: 16 }}
                          />
                        }
                        label={Number(restaurant.rating || 0).toFixed(1)}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          bgcolor: "#fff",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          "& .MuiChip-icon": { ml: 0.5 },
                        }}
                      />

                      {/* Status chip */}
                      {restaurant.isOpen !== undefined && (
                        <Chip
                          label={restaurant.isOpen ? "Open" : "Closed"}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            color: "#fff",
                            bgcolor: restaurant.isOpen
                              ? "rgba(34, 197, 94, 0.95)"
                              : "rgba(107, 114, 128, 0.95)",
                            backdropFilter: "blur(4px)",
                          }}
                        />
                      )}

                      {/* City overlay */}
                      {restaurant.city && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{
                            position: "absolute",
                            bottom: 12,
                            left: 12,
                            color: "#fff",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          <LocationOnIcon sx={{ fontSize: 16 }} />
                          <span>
                            {restaurant.city}
                            {restaurant.state ? `, ${restaurant.state}` : ""}
                          </span>
                        </Stack>
                      )}
                    </Box>

                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                        p: 2.5,
                        "&:last-child": { pb: 2.5 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        noWrap
                        sx={{
                          mb: 0.5,
                          fontSize: "1.05rem",
                          color: "#1a1a1a",
                        }}
                      >
                        {restaurant.restaurantName || "RestaurantWala"}
                      </Typography>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ mb: 1.5 }}
                      >
                        <Rating
                          value={Number(restaurant.rating || 0)}
                          precision={0.1}
                          size="small"
                          readOnly
                          sx={{
                            "& .MuiRating-iconFilled": {
                              color: "#FFB400",
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {restaurant.totalRatings > 0
                            ? `(${restaurant.totalRatings} reviews)`
                            : "No reviews yet"}
                        </Typography>
                      </Stack>

                      <Divider
                        sx={{ mb: 1.5, borderColor: "rgba(0,0,0,0.06)" }}
                      />

                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="flex-start"
                        sx={{ mb: 2, flexGrow: 1 }}
                      >
                        <LocationOnIcon
                          sx={{
                            fontSize: 16,
                            color: THEME.primary,
                            mt: 0.2,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.82rem",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {restaurant.restaurantAddress ||
                            restaurant.location ||
                            "Address not available"}
                        </Typography>
                      </Stack>

                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<RestaurantMenuIcon />}
                        // onClick={() => handleViewMenu(restaurant.slug || restaurant._id)}
                        onClick={() => handleViewMenu(restaurant)}
                        sx={{
                          mt: "auto",
                          borderRadius: 2.5,
                          textTransform: "none",
                          fontWeight: 700,
                          py: 1.1,
                          fontSize: "0.9rem",
                          background: THEME.gradient,
                          boxShadow: "0 8px 20px -8px rgba(255,107,0,0.6)",
                          "&:hover": {
                            background: THEME.primaryDark,
                            boxShadow: "0 10px 24px -8px rgba(255,107,0,0.8)",
                          },
                        }}
                      >
                        View Menu
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    py: 8,
                    textAlign: "center",
                    borderRadius: 4,
                    bgcolor: "#fff",
                    border: "1px dashed rgba(0,0,0,0.1)",
                  }}
                >
                  <LocalDiningIcon
                    sx={{
                      fontSize: 64,
                      color: THEME.primary,
                      opacity: 0.4,
                      mb: 2,
                    }}
                  />
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                    No restaurants found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try changing your search, state or city filter
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>

          {/* ✅ Infinite scroll sentinel + loader */}
          {filteredRestaurants.length > 0 && (
            <Box
              ref={loadMoreRef}
              sx={{
                mt: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 60,
              }}
            >
              {loading && page > 1 && (
                <CircularProgress size={32} sx={{ color: THEME.primary }} />
              )}

              {!hasMore && !loading && page > 1 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  🎉 You've reached the end
                </Typography>
              )}
            </Box>
          )}
        </Container>
      </Box>

      <Footer />
    </>
  );
};

export default Restaurant;
